import { createAsyncThunk } from '@reduxjs/toolkit';
import apiInstance from '@api/index';
import {
  CreateJounralEntryParams,
  DeleteJournalEntryParams,
  DeleteJournalEntriesByTypeParams,
  DeleteJournalEntriesByTypeResponse,
  GetJournalEntriesParams,
  TradeData,
} from '../types';
import toastMessage from '@components/Toast';

export const getJournalEntries = createAsyncThunk<
  TradeData,
  GetJournalEntriesParams,
  { rejectValue: string }
>('journalEntries/getJournalEntries', async (params, { rejectWithValue }) => {
  try {
    toastMessage.info('Loading Journal Entries..');
    const response = await apiInstance.journalEntries.getJournalEntries({
      tradeIds: params.tradeIds,
      compEntities: params.compEntities,
    });
    if (response && response.data.success === true) {
      toastMessage.success('Journal Entries loaded successfully');
    }
    return response.data;
  } catch (error) {
    if (error instanceof Error) {
      toastMessage.error('Failed to load journal entries. ' + error.message);
      return rejectWithValue('Failed to load journal entries.');
    }
    return rejectWithValue('Failed to load journal entries');
  }
});

export const createJournalEntries = createAsyncThunk<
  any,
  { params: CreateJounralEntryParams; getJE: GetJournalEntriesParams },
  { rejectValue: string }
>(
  'journalEntries/createJournalEntries',
  async ({ params, getJE }, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.journalEntries.createJournalEntry(params);

      if (response.data && response.data.success === true) {
        toastMessage.success('Journal Entries created successfully');
        const updatedJEData =
          await apiInstance.journalEntries.getJournalEntries(getJE);
        if (updatedJEData.data && updatedJEData.data.success === true) {
          toastMessage.success('Journal Entries loaded successfully');
        }
        return updatedJEData.data;
      } else {
        toastMessage.error('Failed to load updated journal entries');
        return rejectWithValue('Failed to load updated journal entries');
      }
    } catch (error) {
      if (error instanceof Error) {
        toastMessage.error(
          'Failed to create journal entries. ' + error.message,
        );
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to create journal entries');
    }
  },
);

export const deleteJournalEntry = createAsyncThunk<
  any,
  { params: DeleteJournalEntryParams; getJE: GetJournalEntriesParams },
  { rejectValue: string }
>(
  'journalEntries/deleteJournalEntry',
  async ({ params, getJE }, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.journalEntries.deleteJournalEntry(params);
      if (response.data && response.data.success === true) {
        toastMessage.success('Journal Entry deleted successfully');
        const updatedJEData =
          await apiInstance.journalEntries.getJournalEntries(getJE);
        if (updatedJEData.data && updatedJEData.data.success === true) {
          toastMessage.success('Journal Entries loaded successfully');
        }
        return updatedJEData.data;
      } else {
        toastMessage.error('Failed to load updated journal entries');
        return rejectWithValue('Failed to load updated journal entries');
      }
    } catch (error) {
      if (error instanceof Error) {
        toastMessage.error(
          'Failed to delete journal entries. ' + error.message,
        );
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to delete journal entry');
    }
  },
);

export const getJETransactions = createAsyncThunk<
  number[],
  void,
  { rejectValue: string }
>('journalEntries/getJETransactions', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.journalEntries.getJETransactions();
    if (response.data && response.data.success === true) {
      return response.data.data;
    }
    return rejectWithValue('Failed to load journal entry transactions');
  } catch (error) {
    if (error instanceof Error) {
      toastMessage.error(
        'Failed to load journal entry transactions. ' + error.message,
      );
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to load journal entry transactions');
  }
});

export const getJournalEntryTypes = createAsyncThunk<[], void>(
  'journalEntries/getJournalEntryTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiInstance.journalEntries.getJournalEntryTypes();
      if (response.data && response.data.success === true) {
        return response.data.data;
      } else {
        return rejectWithValue('Failed to load journal entry types');
      }
    } catch (error) {
      if (error instanceof Error) {
        toastMessage.error(
          'Failed to load journal entry types. ' + error.message,
        );
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to load journal entry types');
    }
  },
);

export const deleteJournalEntriesByType = createAsyncThunk<
  TradeData,
  {
    params: DeleteJournalEntriesByTypeParams;
    getJE: GetJournalEntriesParams;
  },
  { rejectValue: string }
>(
  'journalEntries/deleteJournalEntriesByType',
  async ({ params, getJE }, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.journalEntries.deleteJournalEntriesByType(params);
      if (response.data && response.data.success === true) {
        const deletedData = response.data.data;
        toastMessage.success(
          `Successfully deleted ${deletedData.total_deleted} journal entries`,
        );
        const updatedJEData =
          await apiInstance.journalEntries.getJournalEntries(getJE);
        if (updatedJEData.data && updatedJEData.data.success === true) {
          toastMessage.success('Journal Entries reloaded successfully');
        }
        return updatedJEData.data;
      } else {
        toastMessage.error('Failed to delete journal entries by type');
        return rejectWithValue('Failed to delete journal entries by type');
      }
    } catch (error) {
      if (error instanceof Error) {
        toastMessage.error(
          'Failed to delete journal entries by type. ' + error.message,
        );
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to delete journal entries by type');
    }
  },
);
